import {
  collectFilesFromDataTransfer,
  pathedFilesFromFileList,
} from "./input/folder-source.js";
import type { BundleFile } from "./fhir/bundle.js";
import { uploadBundles, type UploadProgress } from "./upload/uploader.js";
import type {
  WorkerBundleSummary,
  WorkerRequest,
  WorkerResponse,
} from "./worker/converter.worker.js";

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
};

const dropzone = $<HTMLElement>("dropzone");
const fileInput = $<HTMLInputElement>("file-input");
const folderInput = $<HTMLInputElement>("folder-input");
const participantInput = $<HTMLInputElement>("participant-id");
const resolutionSelect = $<HTMLSelectElement>("resolution");
const rawCheckbox = $<HTMLInputElement>("include-raw");
const statusPanel = $<HTMLElement>("status");
const statusTitle = $<HTMLElement>("status-title");
const statusLog = $<HTMLPreElement>("status-log");
const progressBar = $<HTMLElement>("progress-bar");
const resultPanel = $<HTMLElement>("result");
const resultSummary = $<HTMLDListElement>("result-summary");
const downloadButton = $<HTMLButtonElement>("download-button");
const uploadButton = $<HTMLButtonElement>("upload-button");
const uploadPanel = $<HTMLDetailsElement>("upload-panel");
const uploadBaseUrl = $<HTMLInputElement>("fhir-base-url");
const uploadToken = $<HTMLInputElement>("fhir-token");
const uploadLog = $<HTMLPreElement>("upload-log");

let downloadHandler: (() => void) | null = null;
let latestBundles: WorkerBundleSummary[] | null = null;

function showStatus(title: string) {
  statusPanel.hidden = false;
  resultPanel.hidden = true;
  statusTitle.textContent = title;
  statusLog.textContent = "";
  progressBar.style.width = "0%";
}

function appendLog(line: string) {
  statusLog.textContent += line + "\n";
  statusLog.scrollTop = statusLog.scrollHeight;
}

function setProgress(completed: number | undefined, total: number | undefined) {
  if (completed != null && total != null && total > 0) {
    progressBar.style.width = `${Math.min(100, Math.round((completed / total) * 100))}%`;
  }
}

function showResult(payload: Extract<WorkerResponse, { type: "done" }>["payload"]) {
  statusPanel.hidden = true;
  resultPanel.hidden = false;
  resultSummary.innerHTML = "";
  const rows: Array<[string, string]> = [
    ["Trial", payload.trialName],
    ["Device", payload.deviceName],
    ["Bundles", payload.bundleCount.toLocaleString()],
    ["Observations", payload.observationCount.toLocaleString()],
    ["File name", payload.filename],
  ];
  for (const [k, v] of rows) {
    const dt = document.createElement("dt");
    dt.textContent = k;
    const dd = document.createElement("dd");
    dd.textContent = v;
    resultSummary.append(dt, dd);
  }
  const blob = new Blob([payload.zip], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  downloadHandler = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = payload.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  latestBundles = payload.bundles;
  uploadLog.hidden = true;
  uploadLog.textContent = "";
}

function appendUploadLog(line: string) {
  uploadLog.hidden = false;
  uploadLog.textContent += line + "\n";
  uploadLog.scrollTop = uploadLog.scrollHeight;
}

function describeBundleProgress(event: Extract<UploadProgress, { type: "bundle" }>): string {
  const parts = [
    `[${event.completed}/${event.total}]`,
    event.filename,
    `HTTP ${event.httpStatus}`,
    `created=${event.created}`,
    `existed=${event.existed}`,
  ];
  if (event.failed) parts.push(`FAILED=${event.failed}`);
  return parts.join("  ");
}

async function handleUpload() {
  if (!latestBundles) {
    appendUploadLog("No bundles to upload. Run a conversion first.");
    return;
  }
  const baseUrl = uploadBaseUrl.value.trim();
  const token = uploadToken.value.trim();
  if (!baseUrl) {
    appendUploadLog("FHIR base URL is required.");
    uploadPanel.open = true;
    uploadBaseUrl.focus();
    return;
  }
  if (!token) {
    appendUploadLog("Bearer token is required.");
    uploadPanel.open = true;
    uploadToken.focus();
    return;
  }

  uploadButton.disabled = true;
  uploadPanel.open = true;
  uploadLog.hidden = false;
  uploadLog.textContent = "";

  const bundles: BundleFile[] = latestBundles.map((b) => ({
    filename: b.filename,
    phase: b.phase,
    json: b.json,
    bundle: JSON.parse(b.json),
  }));

  try {
    appendUploadLog(`Uploading ${bundles.length} bundle(s) to ${baseUrl}...`);
    const result = await uploadBundles({
      baseUrl,
      token,
      bundles,
      onProgress: (event) => {
        if (event.type === "bundle") {
          appendUploadLog(describeBundleProgress(event));
        } else if (event.type === "error") {
          appendUploadLog(`ERROR ${event.filename}: ${event.message}`);
        }
      },
    });
    appendUploadLog(
      `\nDone. created=${result.totals.created} existed=${result.totals.existed} failed=${result.totals.failed}`,
    );
  } catch (err) {
    appendUploadLog(`Upload failed: ${(err as Error).message}`);
  } finally {
    uploadButton.disabled = false;
  }
}

function showError(message: string) {
  statusPanel.hidden = false;
  resultPanel.hidden = true;
  statusTitle.textContent = "Conversion failed";
  appendLog(message);
}

function validateParticipantId(): string | null {
  const value = participantInput.value.trim();
  if (!value) {
    participantInput.focus();
    showError("Please enter a Participant ID before dropping data.");
    return null;
  }
  return value;
}

let worker: Worker | null = null;
function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./worker/converter.worker.ts", import.meta.url), {
    type: "module",
  });
  worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
    const msg = event.data;
    if (msg.type === "progress") {
      const { stage, detail, completed, total } = msg.event;
      const line = detail
        ? `${stage} - ${detail}${total ? ` (${completed ?? 0}/${total})` : ""}`
        : `${stage}`;
      appendLog(line);
      setProgress(completed, total);
    } else if (msg.type === "bundle") {
      appendLog(`  → ${msg.file.filename} (${(msg.file.json.length / 1024).toFixed(0)} KB)`);
    } else if (msg.type === "done") {
      progressBar.style.width = "100%";
      showResult(msg.payload);
    } else if (msg.type === "error") {
      showError(msg.message);
    }
  });
  return worker;
}

function startZipConversion(file: File) {
  const participantId = validateParticipantId();
  if (!participantId) return;
  showStatus(`Reading ${file.name}...`);
  appendLog(`Loaded ZIP: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
  file.arrayBuffer().then((buf) => {
    const w = ensureWorker();
    const req: WorkerRequest = {
      type: "convert",
      payload: {
        source: { kind: "zip", data: buf, filename: file.name },
        participantId,
        resolution: resolutionSelect.value as "native" | "second" | "minute" | "hour",
        includeRawSignals: rawCheckbox.checked,
      },
    };
    w.postMessage(req, [buf]);
  });
}

function startFolderConversion(files: File[]) {
  const participantId = validateParticipantId();
  if (!participantId) return;
  if (files.length === 0) {
    showError("No files were found in the dropped folder.");
    return;
  }
  // Capture webkitRelativePath before the structured-clone inside postMessage
  // strips it on the way to the worker.
  const pathed = pathedFilesFromFileList(files);
  showStatus(`Reading ${pathed.length} files...`);
  for (const p of pathed.slice(0, 10)) appendLog(`- ${p.path}`);
  if (pathed.length > 10) appendLog(`... and ${pathed.length - 10} more files`);
  const w = ensureWorker();
  const req: WorkerRequest = {
    type: "convert",
    payload: {
      source: { kind: "folder", files: pathed },
      participantId,
      resolution: resolutionSelect.value as "native" | "second" | "minute" | "hour",
      includeRawSignals: rawCheckbox.checked,
    },
  };
  w.postMessage(req);
}

dropzone.addEventListener("dragover", (e) => {
  e.preventDefault();
  dropzone.classList.add("is-dragover");
});
dropzone.addEventListener("dragleave", () => dropzone.classList.remove("is-dragover"));
dropzone.addEventListener("drop", async (e) => {
  e.preventDefault();
  dropzone.classList.remove("is-dragover");
  const dt = e.dataTransfer;
  if (!dt) return;
  const items = dt.items;
  if (items && items.length) {
    const files = await collectFilesFromDataTransfer(items);
    if (files.length === 1 && files[0].name.toLowerCase().endsWith(".zip")) {
      startZipConversion(files[0]);
    } else {
      startFolderConversion(files);
    }
  } else if (dt.files.length === 1 && dt.files[0].name.toLowerCase().endsWith(".zip")) {
    startZipConversion(dt.files[0]);
  }
});

fileInput.addEventListener("change", () => {
  const f = fileInput.files?.[0];
  if (f) startZipConversion(f);
  fileInput.value = "";
});

folderInput.addEventListener("change", () => {
  const list = folderInput.files;
  if (!list) return;
  startFolderConversion(Array.from(list));
  folderInput.value = "";
});

downloadButton.addEventListener("click", () => downloadHandler?.());
uploadButton.addEventListener("click", () => {
  void handleUpload();
});
